import React from "react";
import {
  LayoutDashboard,
  Settings,
  Bell,
  QrCode,
  Sticker,
  ChevronRight,
  Trash2,
  Flag,
  LinkIcon,
  FolderTree,
  Package,
  Gift,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";

type SidebarProps = {
  sidebarToggle: boolean;
};

const Sidebar: React.FC<SidebarProps> = ({ sidebarToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { name: "dashboard", path: "/", label: "Dashboard", icon: LayoutDashboard },
    {
      name: "qr-code-designs",
      path: "/qr-code-designs",
      label: "QR Code Designs",
      icon: QrCode,
    },
    {
      name: "sticker-categories",
      path: "/sticker-categories",
      label: "Sticker Categories",
      icon: Sticker,
    },
    {
      name: "delete-requests",
      path: "/delete-requests",
      label: "Delete Requests",
      icon: Trash2,
    },
    {
      name: "sticker-reports",
      path: "/sticker-reports",
      label: "Sticker Reports",
      icon: Flag,
    },
    {
      name: "smart-link-reports",
      path: "/smart-link-reports",
      label: "Smart Link Reports",
      icon: LinkIcon,
    },
  ];

  // Surprise QR Menu Items
  const surpriseQrItems = [
    {
      name: "surprise-categories",
      path: "/surprise-categories",
      label: "Categories",
      icon: FolderTree,
    },
    {
      name: "surprise-content",
      path: "/surprise-content",
      label: "Content",
      icon: Package,
    },
    {
      name: "surprise-qr-reports",
      path: "/surprise-qr-reports",
      label: "QR Reports",
      icon: Flag,
    },
  ];

  return (
    <div
      className={`fixed top-0 left-0 h-screen bg-white text-gray-800 shadow-2xl transition-transform duration-300 ease-in-out ${
        sidebarToggle ? "-translate-x-full" : "translate-x-0"
      } w-72 border-r border-gray-200 z-30`}
    >
      <div className="flex items-center justify-center py-6 px-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-green-700">
        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden">
          <img src={logo} alt="Whatbox" className="w-10 h-10 object-contain" />
        </div>
        <h1 className="text-2xl font-bold ml-3 text-white tracking-wide">
          Whatbox
        </h1>
      </div>

      {/* Menu */}
      <div className="py-6 px-4 overflow-y-auto h-[calc(100vh-88px)]">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <li key={item.name}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group relative ${
                    isActive
                      ? "bg-green-50 text-green-700"
                      : "hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  {/* Active Marker */}
                  {isActive && (
                    <div className="absolute left-0 w-1 h-8 bg-green-600 rounded-r-full" />
                  )}

                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                      isActive
                        ? "bg-green-100 text-green-600"
                        : "bg-gray-100 text-gray-500 group-hover:bg-gray-200"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>

                  <span className="ml-4 text-sm font-medium flex-1 text-left">
                    {item.label}
                  </span>

                  {isActive && <ChevronRight className="w-4 h-4 opacity-70" />}
                </button>
              </li>
            );
          })}
        </ul>

        {/* Surprise QR Section */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 px-4 mb-3">
            <Gift className="w-4 h-4 text-purple-500" />
            <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">
              Surprise QR
            </span>
          </div>

          <ul className="space-y-1">
            {surpriseQrItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <li key={item.name}>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`w-full flex items-center px-4 py-3 rounded-xl transition-all duration-200 group relative ${
                      isActive
                        ? "bg-purple-50 text-purple-700"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    {/* Active Marker */}
                    {isActive && (
                      <div className="absolute left-0 w-1 h-6 bg-purple-600 rounded-r-full" />
                    )}

                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-lg ${
                        isActive
                          ? "bg-purple-100 text-purple-600"
                          : "bg-gray-100 text-gray-500 group-hover:bg-gray-200"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>

                    <span className="ml-3 text-sm font-medium flex-1 text-left">
                      {item.label}
                    </span>

                    {isActive && (
                      <ChevronRight className="w-3 h-3 opacity-70" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-8 p-4 bg-green-50 rounded-xl border border-green-200">
          <p className="text-xs text-gray-500 text-center">
            Admin Dashboard v2.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
